import Iconify from '@components/media/iconify';
import { Box, Button, Card, CardContent, CardHeader, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { SecondStepFormValues } from '../form/billing-order-new-edit-secondstep-form';
import { DistributionItemsTable } from './distribution-items-table';
import { OrderOVInformation } from '@/sections/management/accounts/orders/forms/components/order-ov-information';
import { IOrderDocument } from '@/types/order-document';

interface LegalEntityDistributionCardProps {
  formValue: SecondStepFormValues;
  onAddDocument?: (legalEntityId: string) => void;
  formValueIndex: number;
}

export const LegalEntityDistributionCard = ({
  formValue,
  onAddDocument,
  formValueIndex,
}: LegalEntityDistributionCardProps) => {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();
  const { legalEntity, items } = formValue;

  const documents = watch(`formValues.${formValueIndex}.documents`) || [];

  const handleDeleteDocument = (document: IOrderDocument) => {
    const currentDocuments = documents || [];
    const updatedDocuments = currentDocuments.filter(
      (doc: IOrderDocument) => doc._id !== document._id
    );
    setValue(`formValues.${formValueIndex}.documents`, updatedDocuments);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        avatar={<Iconify icon="solar:buildings-2-bold-duotone" sx={{ color: 'primary.main' }} />}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{legalEntity.label}</Typography>
            <Typography component="span" color="textSecondary" variant="body2">
              ({legalEntity.tax_number})
            </Typography>
            <Chip
              label={t('Legal Entity')}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="mingcute:add-line" />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onAddDocument?.(legalEntity._id);
              }}
            >
              {t('Add Document')}
            </Button>
          </Box>
        }
      />

      <CardContent sx={{ px: 0, '&:last-child': { pb: 0 } }}>
        <DistributionItemsTable formValueIndex={formValueIndex} items={items} />

        {documents && documents.length > 0 && (
          <Box sx={{ px: 2, pb: 2 }}>
            <OrderOVInformation documents={documents} onDeleteDocument={handleDeleteDocument} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
